import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { setMetaTags } from "@/seo";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import OurWork from "@/pages/OurWork";
import CaseStudy from "@/pages/CaseStudy";
import Partnerships from "@/pages/Partnerships";
import Research from "@/pages/Research";
import Founders from "@/pages/Founders";
import Contact from "@/pages/Contact";
import Insights from "@/pages/Insights";
import InsightArticle from "@/pages/InsightArticle";
import Masterclass from "@/pages/Masterclass";
import Feedback from "@/pages/Feedback";
import Book from "@/pages/Book";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const PAGE_META = {
  "/": {
    title: "The Nutrition Network Africa | Nutrition, Technology & Healthcare Innovation",
    description:
      "The Nutrition Network Africa (TNN Africa) builds and enables nutrition, digital health and healthcare innovations through clinical expertise, technology, product development and strategic partnerships.",
  },
  "/about": {
    title: "About | The Nutrition Network Africa",
    description:
      "The Nutrition Network Africa (TNN Africa) is an African nutrition and health innovation organisation founded in 2021, combining clinical nutrition, digital health, product development, education and strategic partnerships.",
  },
  "/our-work": {
    title: "Our Work | The Nutrition Network Africa",
    description:
      "A portfolio of nutrition and healthcare innovation from TNN Africa: PlateMind, OncoNourish, Lana Clinical, Sito Health and ventures built with partners across Africa.",
  },
  "/partnerships": {
    title: "Partnerships | The Nutrition Network Africa",
    description:
      "Partner with TNN Africa: we work with healthcare organisations, founders, technology companies and research institutions to build nutrition and health innovations in Africa.",
  },
  "/research-innovation": {
    title: "Research & Innovation | The Nutrition Network Africa",
    description:
      "Research and innovation at TNN Africa: nutrition research, digital health, AI-enabled nutrition, clinical decision support and nutrition intelligence for African healthcare.",
  },
  "/founders": {
    title: "Founders | The Nutrition Network Africa",
    description:
      "TNN Africa was founded in 2021 by Tito Ipinmoye and Evi Uwede, building nutrition, digital health and healthcare innovation from clinical practice outward.",
  },
  "/insights": {
    title: "Insights | The Nutrition Network Africa",
    description:
      "Notes and research from The Nutrition Network Africa on nutrition, digital health, AI and building healthcare products people actually use.",
  },
  "/contact": {
    title: "Contact | The Nutrition Network Africa",
    description:
      "Partner with The Nutrition Network Africa. Tell us about your nutrition or healthcare challenge and let's build something people can use.",
  },
  "/masterclass": {
    title: "Positioning for Global Leadership Masterclass | The Nutrition Network Africa",
    description:
      "A live career growth masterclass for dietitians, nutritionists and nutrition students. December 12, 2026, 10:00 AM - 1:00 PM WAT, live online. Founding cohort $35 for the first 20 seats.",
  },
  "/admin": { title: "Admin | The Nutrition Network Africa", description: "TNN Africa admin dashboard." },
  "/admin/login": { title: "Admin Sign In | The Nutrition Network Africa", description: "TNN Africa admin sign in." },
};

function TitleManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/our-work/") || pathname.startsWith("/insights/")) return;
    const meta = PAGE_META[pathname] || PAGE_META["/"];
    setMetaTags({ ...meta, path: pathname });
  }, [pathname]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="min-h-screen bg-tnn-deep flex items-center justify-center" data-testid="admin-loading">
        <span className="font-mono text-xs tracking-widest text-tnn-lilac uppercase">Loading</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function Shell() {
  useLenis();
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <div className="App">
      <ScrollManager />
      <TitleManager />
      {!isAdmin && <Header />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/our-work" element={<OurWork />} />
        <Route path="/our-work/:slug" element={<CaseStudy />} />
        <Route path="/partnerships" element={<Partnerships />} />
        <Route path="/research-innovation" element={<Research />} />
        <Route path="/founders" element={<Founders />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/insights/:slug" element={<InsightArticle />} />
        <Route path="/masterclass" element={<Masterclass />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/book" element={<Book />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isAdmin && <Footer />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
        <Toaster position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
